import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminRegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    role: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const departments = [
    { value: '', label: 'Select Department' },
    { value: 'operations', label: 'Operations' },
    { value: 'customer_service', label: 'Customer Service' },
    { value: 'finance', label: 'Finance' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'it', label: 'IT & Technology' },
    { value: 'management', label: 'Management' }
  ];

  const roles = [
    { value: '', label: 'Select Role' },
    { value: 'admin', label: 'Administrator' },
    { value: 'manager', label: 'Manager' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'agent', label: 'Agent' },
    { value: 'analyst', label: 'Analyst' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.department) {
      newErrors.department = 'Please select a department';
    }

    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 11) {
      newErrors.password = 'Password must be at least 11 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate registration success
      alert('Admin registration request submitted! You will receive an email once your account is approved.');
      navigate('/admin/auth/login');
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const userPlusIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
  );

  return (
    <AuthLayout
      title="Admin Registration"
      subtitle="Request access to the JetScanner administration panel"
      icon={userPlusIcon}
      iconBgColor="bg-jet-blue-100"
      iconColor="text-jet-blue-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="First name"
            required
            error={errors.firstName}
          />
          <FormField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Last name"
            required
            error={errors.lastName}
          />
        </div>

        {/* Contact Information */}
        <FormField
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your work email"
          required
          error={errors.email}
        />

        <FormField
          label="Phone Number"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Enter your phone number"
          required
          error={errors.phone}
        />

        {/* Department and Role */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-jet-gray-700 mb-2">
              Department <span className="text-jet-red-500">*</span>
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none transition-colors ${
                errors.department 
                  ? 'border-jet-red-400 focus:border-jet-red-500' 
                  : 'border-jet-gray-300 focus:border-jet-yellow'
              }`}
              required
            >
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>{dept.label}</option>
              ))}
            </select>
            {errors.department && (
              <p className="mt-1 text-sm text-jet-red-600">{errors.department}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-jet-gray-700 mb-2">
              Role <span className="text-jet-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none transition-colors ${
                errors.role 
                  ? 'border-jet-red-400 focus:border-jet-red-500' 
                  : 'border-jet-gray-300 focus:border-jet-yellow'
              }`}
              required
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-jet-red-600">{errors.role}</p>
            )}
          </div>
        </div>

        {/* Password Fields */}
        <FormField
          label="Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a password"
          required
          error={errors.password}
          showPasswordToggle
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
        />

        <FormField
          label="Confirm Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm your password"
          required
          error={errors.confirmPassword}
          showPasswordToggle
          showPassword={showConfirmPassword}
          onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
        />

        {/* Terms Agreement */}
        <div className="flex items-start">
          <input
            type="checkbox"
            name="agreeToTerms"
            checked={formData.agreeToTerms}
            onChange={handleChange}
            className="mt-1 mr-3 h-4 w-4 text-jet-yellow focus:ring-jet-yellow border-jet-gray-300 rounded"
          />
          <label className="text-sm text-jet-gray-700">
            I agree to the{' '}
            <button
              type="button"
              onClick={() => navigate('/terms-and-conditions')}
              className="text-jet-blue-600 hover:text-jet-blue-700 underline"
            >
              Terms and Conditions
            </button>{' '}
            and{' '}
            <button
              type="button"
              onClick={() => navigate('/privacy-policy')}
              className="text-jet-blue-600 hover:text-jet-blue-700 underline"
            >
              Privacy Policy
            </button>
          </label>
        </div>
        {errors.agreeToTerms && (
          <p className="text-sm text-jet-red-600">{errors.agreeToTerms}</p>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-3">
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Submitting Request..."
        >
          Submit Registration Request
        </LoadingButton>

        {/* Additional Links */}
        <div className="text-center space-y-3">
          <div className="text-sm text-jet-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/admin/auth/login')}
              className="text-jet-blue-600 hover:text-jet-blue-700 font-medium"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Back to Main Site */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            ← Back to Main Site
          </button>
        </div>
      </form>

      {/* Registration Notice */}
      <div className="mt-6 bg-jet-yellow-50 border border-jet-yellow-200 rounded-md p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-jet-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div>
            <h4 className="text-sm font-medium text-jet-yellow-900 mb-1">Registration Review Process</h4>
            <p className="text-sm text-jet-yellow-800">
              All admin registration requests are reviewed by our security team. 
              You will receive an email notification once your account is approved, typically within 24-48 hours.
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AdminRegisterPage;